import { hexToString } from 'viem'
import { Currency, ETHER, Token, currencyEquals } from '@etcswapv2/sdk-legacy'
import { useMemo } from 'react'
import { useSelectedTokenList } from '../state/lists/hooks'
import {
  NEVER_RELOAD,
  useMultipleContractSingleData,
  EthersInterface,
} from '../state/multicall/hooks'
import { useUserAddedTokens } from '../state/user/hooks'
import { isAddress } from '../utils'
import ERC20_INTERFACE, { ERC20_BYTES32_INTERFACE } from '../constants/abis/erc20'

import { useActiveWeb3React } from './index'

// Parse a bytes32 hex string to a regular string (null-terminated)
function parseBytes32String(bytes32: string): string {
  // Remove trailing null bytes and convert to string
  try {
    const str = hexToString(bytes32 as `0x${string}`, { size: 32 })
    // Trim null characters
    return str.replace(/\0/g, '').trim()
  } catch {
    return ''
  }
}

export function useAllTokens(): { [address: string]: Token } {
  const { chainId } = useActiveWeb3React()
  const userAddedTokens = useUserAddedTokens()
  const allTokens = useSelectedTokenList()

  return useMemo(() => {
    if (!chainId) return {}
    return (
      userAddedTokens
        // reduce into all ALL_TOKENS filtered by the current chain
        .reduce<{ [address: string]: Token }>(
          (tokenMap, token) => {
            tokenMap[token.address] = token
            return tokenMap
          },
          // must make a copy because reduce modifies the map, and we do not
          // want to make a copy in every iteration
          { ...allTokens[chainId] }
        )
    )
  }, [chainId, userAddedTokens, allTokens])
}

// Check if currency is included in custom list from user storage
export function useIsUserAddedToken(currency: Currency): boolean {
  const userAddedTokens = useUserAddedTokens()
  return !!userAddedTokens.find((token) => currencyEquals(currency, token))
}

// parse a name or symbol from a token response
const BYTES32_REGEX = /^0x[a-fA-F0-9]{64}$/
function parseStringOrBytes32(
  str: string | undefined,
  bytes32: string | undefined,
  defaultValue: string
): string {
  return str && str.length > 0
    ? str
    : bytes32 && BYTES32_REGEX.test(bytes32)
      ? parseBytes32String(bytes32)
      : defaultValue
}

/**
 * Returns a Token object for a given address.
 *
 * Uses useMultipleContractSingleData with ERC20_INTERFACE directly
 * instead of useSingleCallResult with a viem contract, because viem
 * contracts don't have the .interface property that multicall hooks expect.
 *
 * @param tokenAddress - The address of the token
 * @returns Token if found, null if loading, undefined if invalid/not found
 */
export function useToken(tokenAddress?: string): Token | undefined | null {
  const { chainId } = useActiveWeb3React()
  const tokens = useAllTokens()

  const address = isAddress(tokenAddress)

  // Try checksummed address first, then lowercase for case-insensitive matching
  let token: Token | undefined = address ? tokens[address] : undefined
  if (!token && address) {
    // Fallback: try lowercase key lookup (token list may have different casing)
    const lowercaseAddress = address.toLowerCase()
    token = Object.values(tokens).find((t) => t.address.toLowerCase() === lowercaseAddress)
  }

  // Build addresses array - empty if token already found or no address
  const addresses = useMemo(
    () => (token || !address ? [] : [address]),
    [token, address]
  )

  // Use the working pattern: pass Interface directly instead of viem contract
  // Standard ERC20 calls
  const tokenNameResults = useMultipleContractSingleData(
    addresses,
    ERC20_INTERFACE as unknown as EthersInterface,
    'name',
    undefined,
    NEVER_RELOAD
  )
  const symbolResults = useMultipleContractSingleData(
    addresses,
    ERC20_INTERFACE as unknown as EthersInterface,
    'symbol',
    undefined,
    NEVER_RELOAD
  )
  const decimalsResults = useMultipleContractSingleData(
    addresses,
    ERC20_INTERFACE as unknown as EthersInterface,
    'decimals',
    undefined,
    NEVER_RELOAD
  )

  // Bytes32 fallback calls for non-standard tokens
  const tokenNameBytes32Results = useMultipleContractSingleData(
    addresses,
    ERC20_BYTES32_INTERFACE as unknown as EthersInterface,
    'name',
    undefined,
    NEVER_RELOAD
  )
  const symbolBytes32Results = useMultipleContractSingleData(
    addresses,
    ERC20_BYTES32_INTERFACE as unknown as EthersInterface,
    'symbol',
    undefined,
    NEVER_RELOAD
  )

  // Get first result from each call
  const tokenName = tokenNameResults[0]
  const tokenNameBytes32 = tokenNameBytes32Results[0]
  const symbol = symbolResults[0]
  const symbolBytes32 = symbolBytes32Results[0]
  const decimals = decimalsResults[0]

  return useMemo(() => {
    if (token) return token
    if (!chainId || !address) return undefined
    if (decimals?.loading || symbol?.loading || tokenName?.loading) return null
    if (decimals?.result) {
      return new Token(
        chainId,
        address,
        Number(decimals.result[0]),
        parseStringOrBytes32(
          symbol?.result?.[0] as string | undefined,
          symbolBytes32?.result?.[0] as string | undefined,
          'UNKNOWN'
        ),
        parseStringOrBytes32(
          tokenName?.result?.[0] as string | undefined,
          tokenNameBytes32?.result?.[0] as string | undefined,
          'Unknown Token'
        )
      )
    }
    return undefined
  }, [
    address,
    chainId,
    decimals?.loading,
    decimals?.result,
    symbol?.loading,
    symbol?.result,
    symbolBytes32?.result,
    token,
    tokenName?.loading,
    tokenName?.result,
    tokenNameBytes32?.result,
  ])
}

export function useCurrency(currencyId: string | undefined): Currency | null | undefined {
  const isETH = currencyId?.toUpperCase() === 'ETC'
  const token = useToken(isETH ? undefined : currencyId)
  return isETH ? ETHER : token
}
